/**
 * POSControlledSubstanceGate — controlled drugs need a saved customer, full details,
 * a verified Ghana Card, a prescription photo, and a pharmacist's approval before sale.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ShieldAlert, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { usePermission } from '../../hooks/usePermission';
import { CustomerPicker } from './controlled/CustomerPicker';
import { GhanaCardCheck } from './controlled/GhanaCardCheck';
import { RequestForm, emptyRequest } from './controlled/RequestForm';
import { PharmacistReview, usePollRequest } from './controlled/PharmacistReview';
import { errText } from './controlled/errText';

const REQUIRED = ['full_name', 'phone', 'address', 'ghana_card_number', 'prescriber_name', 'prescriber_licence', 'prescription_date'];

export const POSControlledSubstanceGate = ({ cart, open, onOpenChange, onApproved, selectedPatientId }) => {
  const { allowed: canApprove } = usePermission('rx_approve');
  const [patientId, setPatientId] = useState(null);
  const [identity, setIdentity] = useState(null);
  const [form, setForm] = useState(emptyRequest);
  const [photo, setPhoto] = useState(null);
  const [request, setRequest] = useState(null);
  const [fastTrack, setFastTrack] = useState(false);
  const [saving, setSaving] = useState(false);
  const controlledItems = useMemo(() => cart.filter((item) => item.is_controlled), [cart]);
  usePollRequest(request, setRequest);

  const loadCustomer = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/controlled/identity/${id}`);
      setPatientId(id);
      setIdentity(data.identity);
      setFastTrack(!!data.fast_track);
      setForm((f) => ({ ...f, full_name: f.full_name || data.patient.name, phone: f.phone || data.patient.phone,
        address: f.address || data.patient.address, ghana_card_number: f.ghana_card_number || data.ghana_card_number }));
    } catch (e) { toast.error(errText(e, 'Could not load the customer')); }
  }, []);

  useEffect(() => {
    if (open && !patientId && selectedPatientId) loadCustomer(selectedPatientId);
  }, [open, patientId, selectedPatientId, loadCustomer]);

  const reset = () => { setPatientId(null); setIdentity(null); setForm(emptyRequest); setPhoto(null); setRequest(null); setFastTrack(false); };
  const close = () => { onOpenChange(false); };

  const submit = async () => {
    const missing = REQUIRED.filter((k) => !String(form[k] || '').trim());
    if (missing.length || !photo) { toast.error('Fill every field marked * and add the prescription photo'); return; }
    const body = new FormData();
    Object.entries(form).forEach(([k, v]) => body.append(k, String(v).trim()));
    body.append('patient_id', patientId);
    body.append('product_ids', JSON.stringify(controlledItems.map((i) => i.product_id)));
    body.append('prescription_photo', photo);
    setSaving(true);
    try { const { data } = await api.post('/controlled/requests', body, { headers: { 'Content-Type': 'multipart/form-data' } }); setRequest(data); toast.success('Sent for pharmacist approval'); }
    catch (e) { toast.error(errText(e, 'Could not submit')); }
    finally { setSaving(false); }
  };

  const proceed = () => {
    onApproved(request.approval_id, { patient_id: patientId, name: request.full_name, phone: request.phone });
    reset();
    onOpenChange(false);
  };

  if (controlledItems.length === 0) return null;
  const startId = patientId || selectedPatientId;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="controlled-substance-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600"><ShieldAlert className="h-5 w-5" /> Controlled drug: verify the customer</DialogTitle>
        </DialogHeader>
        <div className="flex flex-wrap gap-1" data-testid="controlled-items-alert">
          {controlledItems.map((item) => <Badge key={item.product_id} variant="outline" className="text-[10px] border-amber-300 text-amber-700">{item.product_name}</Badge>)}
        </div>

        {!startId && <CustomerPicker onPick={loadCustomer} />}

        {patientId && !request && (
          <div className="space-y-4">
            <RequestForm form={form} setForm={setForm} setPhoto={setPhoto} fastTrack={fastTrack} />
            <GhanaCardCheck patientId={patientId} identity={identity} form={form} canApprove={canApprove} onChanged={() => loadCustomer(patientId)} />
            <DialogFooter>
              <Button variant="outline" onClick={() => { reset(); close(); }}>Cancel</Button>
              <Button onClick={submit} disabled={saving} className="bg-amber-600 hover:bg-amber-500 text-white" data-testid="ctrl-submit-btn">
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Send for pharmacist approval
              </Button>
            </DialogFooter>
          </div>
        )}

        {request?.status === 'pending' && (
          <div className="space-y-3">
            <GhanaCardCheck patientId={patientId} identity={request.identity} form={form} canApprove={canApprove}
              onChanged={() => api.get(`/controlled/requests/${request.request_id}`).then((r) => setRequest(r.data))} />
            <PharmacistReview request={request} canApprove={canApprove} onDecided={setRequest} />
          </div>
        )}

        {request?.status === 'approved' && (
          <div className="space-y-3 text-center py-3" data-testid="ctrl-approved">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
            <p className="font-semibold text-emerald-700">Approved by the pharmacist</p>
            <Button onClick={proceed} className="w-full" data-testid="ctrl-proceed-btn">Proceed to checkout</Button>
          </div>
        )}

        {request?.status === 'rejected' && (
          <div className="space-y-3 text-center py-3" data-testid="ctrl-rejected">
            <XCircle className="h-12 w-12 text-red-500 mx-auto" />
            <p className="font-semibold text-red-700">Not approved</p>
            <p className="text-sm text-muted-foreground">{request.decision_reason}</p>
            <Button variant="outline" onClick={() => { reset(); close(); }}>Close</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
